// Libraries
import React from 'react';
import classNames from 'classnames';
import { CellContext, ColumnDef } from '@tanstack/react-table';

// Components
import { CurrencyCell, DateCell, GrowthCell, ObjectiveCell, PercentageCell, StatCell, TextCell } from '@/views/components/cells';

// Enums
import { PORTFOLIO_METRICS_TABLE_COLUMNS } from '@/enums/Portfolio';

// Types
import Locale from '@/types/locale';

// Utils
import { skipFirstCustomSorting } from '@/utils/data-table';
import { getCellValue, getDisclaimer, getHeader, Metrics } from './metrics-table';
import { LOCALE } from '@/utils/i18n';

type setTooltipText = React.Dispatch<React.SetStateAction<string>>;
interface ColumnGroupMeta {
  customClass: string;
}

interface ColumnDefinition {
  id: PORTFOLIO_METRICS_TABLE_COLUMNS;
  accessorFn?: (row: Metrics) => any;
  accessorKey?: PORTFOLIO_METRICS_TABLE_COLUMNS;
  cell: (info: CellContext<Metrics, any>) => JSX.Element;
  enablePinning: boolean;
  header: string;
  meta?: ColumnGroupMeta;
  size: number;
  sortingFn: (rowA: any, rowB: any, columnId: string) => number;
}

interface ColumnGroup {
  id: string;
  header: string;
  meta?: ColumnGroupMeta;
  columns: ColumnDefinition[];
}

export enum ColumnGroupId {
  FIXED_GROUP = 'FixedGroup',
  KEY_METRICS = 'KeyMetrics',
  ALTERNATIVE_OVERVIEW = 'Alternative overview',
  FEE_INFORMATION = 'Fee information',
  ALTERNATIVE_PERFORMANCE = 'Alternative Performance',
}

export const getTableColumns = (setTooltipText: setTooltipText, locale?: Locale): ColumnDef<Metrics>[] => {
  const baseColumns: ColumnGroup[] = [
    {
      id: ColumnGroupId.FIXED_GROUP,
      header: '',
      columns: [
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.CODE,
          accessorKey: PORTFOLIO_METRICS_TABLE_COLUMNS.CODE,
          cell: info => getCellValue(info),
          enablePinning: true,
          header: getHeader('Code'),
          size: 160,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.NAME,
          accessorKey: PORTFOLIO_METRICS_TABLE_COLUMNS.NAME,
          cell: info => getTruncatedTextCell(info, setTooltipText),
          enablePinning: true,
          header: getHeader('Name'),
          size: 320,
          sortingFn: skipFirstCustomSorting,
        },
      ],
    },
    {
      id: ColumnGroupId.KEY_METRICS,
      header: getHeader('KeyMetrics'),
      meta: {
        customClass: 'group-left-border',
      },
      columns: [
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.ONE_YEAR,
          accessorFn: row => row.KEY_METRICS?.[PORTFOLIO_METRICS_TABLE_COLUMNS.ONE_YEAR],
          cell: info => getGrowthCell(info),
          enablePinning: false,
          header: getHeader('1YR'),
          meta: {
            customClass: 'group-left-border',
          },
          size: 88,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.THREE_YEAR,
          accessorFn: row => row.KEY_METRICS?.[PORTFOLIO_METRICS_TABLE_COLUMNS.THREE_YEAR],
          cell: info => getGrowthCell(info),
          enablePinning: false,
          header: getHeader('3YR'),
          size: 88,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.BETA,
          accessorFn: row => row.KEY_METRICS?.[PORTFOLIO_METRICS_TABLE_COLUMNS.BETA],
          cell: info => getStatCell(info),
          enablePinning: false,
          header: getHeader('Beta'),
          size: 88,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.MER,
          accessorFn: row => row.KEY_METRICS?.[PORTFOLIO_METRICS_TABLE_COLUMNS.MER],
          cell: info => getPercentageCell(info),
          enablePinning: false,
          header: getHeader('MER'),
          size: locale === LOCALE.EN_US ? 125 : 88,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.CLASSIFICATION,
          accessorFn: row => row.KEY_METRICS?.[PORTFOLIO_METRICS_TABLE_COLUMNS.CLASSIFICATION],
          cell: info => getTruncatedTextCell(info, setTooltipText),
          enablePinning: false,
          header: getHeader('Classification'),
          size: 144,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.OBJECTIVE,
          accessorFn: row => row.KEY_METRICS?.[PORTFOLIO_METRICS_TABLE_COLUMNS.OBJECTIVE],
          cell: info => getObjectiveCell(info),
          enablePinning: false,
          header: getHeader('Objective'),
          size: 144,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.INCEPTION_DATE,
          accessorFn: row => row.KEY_METRICS?.[PORTFOLIO_METRICS_TABLE_COLUMNS.INCEPTION_DATE],
          cell: info => getDateCell(info),
          enablePinning: false,
          header: getHeader('InceptionDate'),
          size: 144,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.TNA,
          accessorFn: row => row.KEY_METRICS?.[PORTFOLIO_METRICS_TABLE_COLUMNS.TNA],
          cell: info => getTNACell(info),
          enablePinning: false,
          header: getHeader('TNA'),
          size: 160,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.CURRENCY,
          accessorFn: row => row.KEY_METRICS?.[PORTFOLIO_METRICS_TABLE_COLUMNS.CURRENCY],
          cell: info => getTextCell(info),
          enablePinning: false,
          header: getHeader('Currency'),
          size: 104,
          sortingFn: skipFirstCustomSorting,
        },
      ],
    },
  ];

  // Alternative-related columns that will only be added if canViewAltsColumns is true
  const alternativeColumns: ColumnGroup[] = [
    {
      id: ColumnGroupId.ALTERNATIVE_OVERVIEW,
      header: getHeader('AlternativeOverview'),
      meta: {
        customClass: 'group-left-border',
      },
      columns: [
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.FIRM_NAME,
          accessorFn: row => row.ALTERNATIVE_OVERVIEW?.[PORTFOLIO_METRICS_TABLE_COLUMNS.FIRM_NAME],
          cell: info => getTextCell(info),
          enablePinning: false,
          header: getHeader('FirmName'),
          meta: {
            customClass: 'group-left-border',
          },
          size: 144,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.VINTAGE,
          accessorFn: row => row.ALTERNATIVE_OVERVIEW?.[PORTFOLIO_METRICS_TABLE_COLUMNS.VINTAGE],
          cell: info => getTextCell(info),
          enablePinning: false,
          header: getHeader('Vintage'),
          size: 88,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.STATUS,
          accessorFn: row => row.ALTERNATIVE_OVERVIEW?.[PORTFOLIO_METRICS_TABLE_COLUMNS.STATUS],
          cell: info => getTextCell(info),
          enablePinning: false,
          header: getHeader('Status'),
          size: 144,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.COMMITMENT,
          accessorFn: row => row.ALTERNATIVE_OVERVIEW?.[PORTFOLIO_METRICS_TABLE_COLUMNS.COMMITMENT],
          cell: info => getCurrencyCell(info),
          enablePinning: false,
          header: getHeader('Commitment'),
          size: 144,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.GP_COMMITMENTS_TO_FUND,
          accessorFn: row => row.ALTERNATIVE_OVERVIEW?.[PORTFOLIO_METRICS_TABLE_COLUMNS.GP_COMMITMENTS_TO_FUND],
          cell: info => getCurrencyCell(info),
          enablePinning: false,
          header: getHeader('GPCommitmentsToFund'),
          size: 200,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.CALLED,
          accessorFn: row => row.ALTERNATIVE_OVERVIEW?.[PORTFOLIO_METRICS_TABLE_COLUMNS.CALLED],
          cell: info => getCurrencyCell(info),
          enablePinning: false,
          header: getHeader('Called'),
          size: 144,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.DISTRIBUTED,
          accessorFn: row => row.ALTERNATIVE_OVERVIEW?.[PORTFOLIO_METRICS_TABLE_COLUMNS.DISTRIBUTED],
          cell: info => getCurrencyCell(info),
          enablePinning: false,
          header: getHeader('Distributed'),
          size: 144,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.REMAINING,
          accessorFn: row => row.ALTERNATIVE_OVERVIEW?.[PORTFOLIO_METRICS_TABLE_COLUMNS.REMAINING],
          cell: info => getCurrencyCell(info),
          enablePinning: false,
          header: getHeader('Remaining'),
          size: 144,
          sortingFn: skipFirstCustomSorting,
        },
      ],
    },
    {
      id: ColumnGroupId.FEE_INFORMATION,
      header: getHeader('FeeInformation'),
      meta: {
        customClass: 'group-left-border',
      },
      columns: [
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.DURING_INVESTMENT_PERIOD,
          accessorFn: row => row.FEE_INFORMATION?.[PORTFOLIO_METRICS_TABLE_COLUMNS.DURING_INVESTMENT_PERIOD],
          cell: info => getGrowthCell(info),
          enablePinning: false,
          header: getHeader('DuringInvestmentPeriod'),
          meta: {
            customClass: 'group-left-border',
          },
          size: 200,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.POST_INVESTMENT_PERIOD,
          accessorFn: row => row.FEE_INFORMATION?.[PORTFOLIO_METRICS_TABLE_COLUMNS.POST_INVESTMENT_PERIOD],
          cell: info => getGrowthCell(info),
          enablePinning: false,
          header: getHeader('PostInvestmentPeriod'),
          size: 200,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.CHARGE_FREQUENCY,
          accessorFn: row => row.FEE_INFORMATION?.[PORTFOLIO_METRICS_TABLE_COLUMNS.CHARGE_FREQUENCY],
          cell: info => getTextCell(info),
          enablePinning: false,
          header: getHeader('ChargeFrequency'),
          size: 200,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.HURDLE_RATE,
          accessorFn: row => row.FEE_INFORMATION?.[PORTFOLIO_METRICS_TABLE_COLUMNS.HURDLE_RATE],
          cell: info => getPercentageCell(info),
          enablePinning: false,
          header: getHeader('HurdleRate'),
          size: 160,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.CARRIED_INTEREST,
          accessorFn: row => row.FEE_INFORMATION?.[PORTFOLIO_METRICS_TABLE_COLUMNS.CARRIED_INTEREST],
          cell: info => getPercentageCell(info),
          enablePinning: false,
          header: getHeader('CarriedInterest'),
          size: 160,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.GP_CATCH_UP_RATE,
          accessorFn: row => row.FEE_INFORMATION?.[PORTFOLIO_METRICS_TABLE_COLUMNS.GP_CATCH_UP_RATE],
          cell: info => getPercentageCell(info),
          enablePinning: false,
          header: getHeader('GPCatchUpRate'),
          size: 160,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.FUND_FORMATION_COST,
          accessorFn: row => row.FEE_INFORMATION?.[PORTFOLIO_METRICS_TABLE_COLUMNS.FUND_FORMATION_COST],
          cell: info => getCurrencyCell(info),
          enablePinning: false,
          header: getHeader('FundFormationCost'),
          size: 200,
          sortingFn: skipFirstCustomSorting,
        },
      ],
    },
    {
      id: ColumnGroupId.ALTERNATIVE_PERFORMANCE,
      header: getHeader('AlternativePerformance'),
      meta: {
        customClass: 'group-left-border',
      },
      columns: [
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.NET_IRR,
          accessorFn: row => row.ALTERNATIVE_PERFORMANCE?.[PORTFOLIO_METRICS_TABLE_COLUMNS.NET_IRR],
          cell: info => getGrowthCell(info),
          enablePinning: false,
          header: getHeader('NetIRR'),
          meta: {
            customClass: 'group-left-border',
          },
          size: 88,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.MEDIAN_BENCHMARK_IRR,
          accessorFn: row => row.ALTERNATIVE_PERFORMANCE?.[PORTFOLIO_METRICS_TABLE_COLUMNS.MEDIAN_BENCHMARK_IRR],
          cell: info => getGrowthCell(info),
          enablePinning: false,
          header: getHeader('MedianBenchmarkIRR'),
          size: 200,
          sortingFn: skipFirstCustomSorting,
        },
        {
          id: PORTFOLIO_METRICS_TABLE_COLUMNS.QUARTILE,
          accessorFn: row => row.ALTERNATIVE_PERFORMANCE?.[PORTFOLIO_METRICS_TABLE_COLUMNS.QUARTILE],
          cell: info => getTextCell(info),
          enablePinning: false,
          header: getHeader('Quartile'),
          size: 144,
          sortingFn: skipFirstCustomSorting,
        },
      ],
    },
  ];

  return [...baseColumns, ...alternativeColumns];
};

const getCellData = (info: CellContext<Metrics, any>) => {
  const value = getCellValue(info);
  const disclaimer = getDisclaimer(info);
  return { value, disclaimer };
};

// Cells content for metrics
const getStatCell = (info: CellContext<Metrics, any>) => {
  const stat = getCellValue(info);
  return <StatCell value={stat} />;
};

const getDateCell = (info: CellContext<Metrics, any>) => {
  const { value, disclaimer } = getCellData(info);
  return <DateCell value={value} disclaimer={disclaimer} />;
};

const getObjectiveCell = (info: CellContext<Metrics, any>) => {
  const { value, disclaimer } = getCellData(info);
  return <ObjectiveCell value={value} linkDisabled={false} showIcon={false} backdropClassName="modal-v2-backdrop" disclaimer={disclaimer} />;
};

const getTNACell = (info: CellContext<Metrics, any>) => {
  const { value, disclaimer } = getCellData(info);
  return <CurrencyCell value={value} disclaimer={disclaimer} />;
};

// TODO Design System V2: Change the "Positive" Color to var(--text-success-default)
// TODO Design System V2: Change the "Negative" Color to var(--text-error-default)
const getGrowthCell = (info: CellContext<Metrics, any>) => {
  const { value, disclaimer } = getCellData(info);
  return <GrowthCell value={value} disclaimer={disclaimer} />;
};

const getPercentageCell = (info: CellContext<Metrics, any>) => {
  const { value, disclaimer } = getCellData(info);
  return <PercentageCell value={value} disclaimer={disclaimer} />;
};

const getCurrencyCell = (info: CellContext<Metrics, any>) => {
  const { value, disclaimer } = getCellData(info);
  return <CurrencyCell value={{ value }} disclaimer={disclaimer} />;
};

const getTextCell = (info: CellContext<Metrics, any>) => {
  const { value, disclaimer } = getCellData(info);
  return <TextCell value={value} disclaimer={disclaimer} />;
};

const getTruncatedTextCell = (info: CellContext<Metrics, any>, setTooltipText: setTooltipText): JSX.Element => {
  const { disclaimer, value } = getCellData(info);

  const NAME_MAX_LIMIT = 34;
  const shouldShowTooltip = value?.length > NAME_MAX_LIMIT;
  const columnSize = info.column.columnDef.size as number;

  const cellClassNames = classNames(`text-cell--w${columnSize}`, shouldShowTooltip ? 'paragraph-with-ellipsis' : '');
  const content = <TextCell value={value} disclaimer={disclaimer} className={cellClassNames} id={info.column.id} />;

  if (!shouldShowTooltip) {
    return content;
  }

  const tooltipHandlers = {
    onMouseEnter: () => setTooltipText(value),
    onMouseLeave: () => setTooltipText(''),
  };

  return <div {...tooltipHandlers}>{content}</div>;
};
